import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Card } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { ChevronDown, ChevronUp } from "lucide-react"
import { ORDERED_PRIMARY_LOGISTICS_LOCALES as ORDERED_PRIMARY_LOGISTICS_LOCALE_DEFAULTS } from "modules/settings/hooks/amphora/guessDefaultLogisticsLocales"
import Locale from "modules/shared/components/Miscelanea/Locale"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AMPHORA_LOGISTICS_TEMPLATE_LOCALES, AmphoraLogisticsTemplateLocale } from "schema"

const allLocaleSet = new Set(AMPHORA_LOGISTICS_TEMPLATE_LOCALES)
const ORDERED_PRIMARY_LOGISTICS_LOCALES: AmphoraLogisticsTemplateLocale[] =
  ORDERED_PRIMARY_LOGISTICS_LOCALE_DEFAULTS.filter((c) => allLocaleSet.has(c))
const REST_LOGISTICS_LOCALES = AMPHORA_LOGISTICS_TEMPLATE_LOCALES.filter(
  (c) => !ORDERED_PRIMARY_LOGISTICS_LOCALES.includes(c),
)

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultLocales: AmphoraLogisticsTemplateLocale[]
  onConfirm: (locales: AmphoraLogisticsTemplateLocale[]) => void
  isPending: boolean
}

export function AmphoraLogisticsWhatsappSetupDialog({
  open,
  onOpenChange,
  defaultLocales,
  onConfirm,
  isPending,
}: Props) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<Set<string>>(() => new Set(defaultLocales))
  const [showAllLocales, setShowAllLocales] = useState(false)

  useEffect(() => {
    if (open) {
      setSelected(new Set(defaultLocales.length ? defaultLocales : ["en"]))
      setShowAllLocales(false)
    }
  }, [open, defaultLocales])

  const toggle = (locale: AmphoraLogisticsTemplateLocale) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(locale)) {
        next.delete(locale)
      } else {
        next.add(locale)
      }
      return next
    })
  }

  const handleConfirm = () => {
    const list = Array.from(selected) as AmphoraLogisticsTemplateLocale[]
    if (list.length === 0) return
    onConfirm(list)
  }

  const visibleLocales: AmphoraLogisticsTemplateLocale[] = showAllLocales
    ? [...ORDERED_PRIMARY_LOGISTICS_LOCALES, ...REST_LOGISTICS_LOCALES]
    : ORDERED_PRIMARY_LOGISTICS_LOCALES
  const canExpand = REST_LOGISTICS_LOCALES.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("settings.integrations.amphora.communications.logisticsDialog.whatsapp.title")}
          </DialogTitle>
          <DialogDescription>
            {t("settings.integrations.amphora.communications.logisticsDialog.whatsapp.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <div className="space-y-1">
            <Label className="text-sm font-medium">
              {t(
                "settings.integrations.amphora.communications.logisticsDialog.whatsapp.languagesLabel",
              )}
            </Label>
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-y-auto overscroll-contain max-h-100">
              <ul className="divide-y divide-border" role="list">
                {visibleLocales.map((locale) => {
                  const id = `amphora-loc-${locale}`
                  return (
                    <li key={locale}>
                      <label
                        htmlFor={id}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors",
                          "hover:bg-muted/60 focus-within:bg-muted/60",
                        )}
                      >
                        <Checkbox
                          id={id}
                          checked={selected.has(locale)}
                          onCheckedChange={() => toggle(locale)}
                          className="shrink-0"
                        />
                        <div className="min-w-0 flex-1 text-sm">
                          <Locale code={locale} />
                        </div>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          </Card>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {t(
                "settings.integrations.amphora.communications.logisticsDialog.whatsapp.selectedCount",
                {
                  count: selected.size,
                },
              )}
            </p>
            {canExpand && (
              <Button
                type="button"
                variant="link"
                className="h-auto shrink-0 gap-1.5 px-0 text-sm"
                onClick={() => setShowAllLocales((v) => !v)}
              >
                {showAllLocales
                  ? t(
                      "settings.integrations.amphora.communications.logisticsDialog.whatsapp.seeLess",
                    )
                  : t(
                      "settings.integrations.amphora.communications.logisticsDialog.whatsapp.seeMore",
                    )}
                {showAllLocales ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </Button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isPending || selected.size === 0}>
            {t("shared.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
